/**
 * Generic, dependency-free markdown stream parsing utilities.
 *
 * These utilities handle the extraction and processing of special-tag content
 * from streaming markdown text (e.g., `<lumina>...</lumina>`, `◴...◶`).
 * They operate on plain strings only — no conversation-assistant-specific
 * types or store dependencies.
 */

export type StreamableComponentConfig = {
	key: string;
	startTag: string;
	endTag: string;
	placeholderTag: string;
	isEnabled: boolean;
};

export type MarkdownData = {
	key: string;
	content: string;
	loading: boolean;
};

export type MarkdownDataMap = Record<string, { content: string; loading: boolean }>;

function escapeRegExp(string: string): string {
	return string.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
}

/**
 * Extracts content between boundary tags from a markdown text string,
 * replacing each matched section with an optional replacement string.
 *
 * @returns The processed text and an array of extracted boundary contents.
 */
export const extractMarkdownText = (
	text: string,
	boundaryStart: string,
	boundaryEnd: string,
	replacementContent: string = '',
): {
	processedText: string;
	boundaryContents: string[];
} => {
	const boundaryContents: string[] = [];

	if (!text) {
		return { processedText: text, boundaryContents };
	}

	const regex = new RegExp(`${escapeRegExp(boundaryStart)}(.*?)${escapeRegExp(boundaryEnd)}`, 'gs');

	let newText = text;
	let match;
	let found = false;

	while ((match = regex.exec(text)) !== null) {
		boundaryContents.push(match[1]);
		newText = newText.replace(match[0], replacementContent);
		found = true;
	}

	return {
		processedText: found ? newText : text,
		boundaryContents,
	};
};

/**
 * Processes a single streaming markdown text chunk for a specific component type.
 * Handles start/end tags arriving in separate chunks during streaming.
 *
 * During streaming, chunks arrive one at a time. This function:
 * - Detects an opening tag and starts tracking the component content
 * - Accumulates content between open/close tags
 * - Finalises extraction when the closing tag arrives
 *
 * @param text - The current chunk's text content
 * @param currentLoadingKey - The currently tracked loading key (null if not inside a tag)
 * @param previousKey - The last completed key for this component type
 * @param componentConfig - Configuration for the component type being processed
 * @returns Updated state and any extracted markdown data
 */
export const processMarkdownComponent = (
	text: string,
	currentLoadingKey: string | null,
	previousKey: string | null,
	componentConfig: StreamableComponentConfig,
): {
	processedText: string;
	currentLoadingKey: string | null;
	previousKey: string | null;
	markdownData: MarkdownData | null;
} => {
	const { startTag, endTag, placeholderTag, key } = componentConfig;
	let streamingMarkdownContent: string = '';
	let newLoadingKey = currentLoadingKey;
	let newPreviousKey = previousKey;
	let markdownData: MarkdownData | null = null;
	let processedText = text;

	if (typeof text === 'string') {
		const hasCloseTag = text.includes(endTag);
		if (hasCloseTag && currentLoadingKey && currentLoadingKey.startsWith(`${key}-`)) {
			const [contentBeforeCloseTag, contentAfterCloseTag] = text.split(endTag);

			streamingMarkdownContent = contentBeforeCloseTag;
			processedText = contentAfterCloseTag;

			markdownData = {
				key: currentLoadingKey,
				content: streamingMarkdownContent,
				loading: false,
			};
			newPreviousKey = currentLoadingKey;
			newLoadingKey = null;
		} else if (text.includes(startTag) && !currentLoadingKey) {
			const currentCount =
				previousKey == null ? -1 : parseInt(previousKey.substring(`${key}-`.length), 10);
			newLoadingKey = `${key}-${currentCount + 1}`;

			const [contentBeforeOpenTag, contentAfterOpenTag] = text.split(startTag);

			processedText = contentBeforeOpenTag + placeholderTag;
			streamingMarkdownContent = contentAfterOpenTag;

			markdownData = {
				key: newLoadingKey,
				content: streamingMarkdownContent,
				loading: true,
			};
			newPreviousKey = newLoadingKey;
		} else if (currentLoadingKey) {
			streamingMarkdownContent = text;
			processedText = '';

			markdownData = {
				key: currentLoadingKey,
				content: streamingMarkdownContent,
				loading: true,
			};
			newPreviousKey = currentLoadingKey;
		}
	}

	return {
		processedText,
		currentLoadingKey: newLoadingKey,
		previousKey: newPreviousKey,
		markdownData,
	};
};

/**
 * Creates a stateful processor that handles streaming markdown text chunks
 * across multiple component types.
 *
 * Returns a function that processes each incoming text chunk, extracting
 * component content from special tags as they arrive in the stream.
 *
 * @param components - The streamable component configurations to process
 * @returns A stateful processor function
 */
export const createMarkdownProcessor = (
	getComponents: () => StreamableComponentConfig[],
): ((text: string) => {
	processedText: string;
	markdownData: MarkdownData | null;
	processedComponentType: string | null;
}) => {
	let currentLoadingKeys: Record<string, string | null> = {};
	let previousKeys: Record<string, string | null> = {};

	return (
		text: string,
	): {
		processedText: string;
		markdownData: MarkdownData | null;
		processedComponentType: string | null;
	} => {
		let processedText = text;
		let newCurrentLoadingKeys = { ...currentLoadingKeys };
		let newPreviousKeys = { ...previousKeys };
		let markdownData: MarkdownData | null = null;
		let processedComponentType: string | null = null;

		for (const component of getComponents()) {
			if (!component.isEnabled) {
				continue;
			}

			const currentLoadingKey = currentLoadingKeys[component.key] || null;
			const previousKey = previousKeys[component.key] || null;

			const result = processMarkdownComponent(
				processedText,
				currentLoadingKey,
				previousKey,
				component,
			);

			if (result.markdownData) {
				processedText = result.processedText;
				newCurrentLoadingKeys[component.key] = result.currentLoadingKey;
				newPreviousKeys[component.key] = result.previousKey;
				markdownData = result.markdownData;
				processedComponentType = component.key;
				break; // Only process one component type per call
			}
		}

		currentLoadingKeys = newCurrentLoadingKeys;
		previousKeys = newPreviousKeys;

		return {
			processedText,
			markdownData,
			processedComponentType,
		};
	};
};

/**
 * Identical to processMarkdownComponent except that when a start tag is found,
 * the content after the tag is returned as `unprocessedText` rather than being
 * embedded in `markdownData.content`. This allows the caller to recursively
 * process `unprocessedText` — which may itself contain a closing tag or further
 * start tags — before committing the final markdown data.
 *
 * Handles the case where multiple tags are in a single chunk.
 */
export const processMarkdownComponentMultiTags = (
	text: string,
	currentLoadingKey: string | null,
	previousKey: string | null,
	componentConfig: StreamableComponentConfig,
): {
	processedText: string;
	unprocessedText: string;
	currentLoadingKey: string | null;
	previousKey: string | null;
	markdownData: MarkdownData | null;
} => {
	const { startTag, endTag, placeholderTag, key } = componentConfig;
	let newLoadingKey = currentLoadingKey;
	let newPreviousKey = previousKey;
	let markdownData: MarkdownData | null = null;
	let processedText = text;
	let unprocessedText = '';

	if (typeof text === 'string') {
		const closeTagIndex = text.indexOf(endTag);
		if (closeTagIndex !== -1 && currentLoadingKey && currentLoadingKey.startsWith(`${key}-`)) {
			const contentBeforeCloseTag = text.substring(0, closeTagIndex);
			const contentAfterCloseTag = text.substring(closeTagIndex + endTag.length);

			processedText = '';
			unprocessedText = contentAfterCloseTag;

			markdownData = {
				key: currentLoadingKey,
				content: contentBeforeCloseTag,
				loading: false,
			};
			newPreviousKey = currentLoadingKey;
			newLoadingKey = null;
		} else if (text.includes(startTag) && !currentLoadingKey) {
			const currentCount =
				previousKey == null ? -1 : parseInt(previousKey.substring(`${key}-`.length), 10);
			newLoadingKey = `${key}-${currentCount + 1}`;

			const openTagIndex = text.indexOf(startTag);
			const contentBeforeOpenTag = text.substring(0, openTagIndex);
			const contentAfterOpenTag = text.substring(openTagIndex + startTag.length);

			processedText = contentBeforeOpenTag + placeholderTag;
			unprocessedText = contentAfterOpenTag;

			markdownData = {
				key: newLoadingKey,
				content: '',
				loading: true,
			};
			newPreviousKey = newLoadingKey;
		} else if (currentLoadingKey) {
			// Mid-stream branch: no boundary in this chunk; the whole text is
			// streamed content for the active component.
			processedText = '';
			unprocessedText = '';

			markdownData = {
				key: currentLoadingKey,
				content: text,
				loading: true,
			};
			newPreviousKey = currentLoadingKey;
		}
	}

	return {
		processedText,
		unprocessedText,
		currentLoadingKey: newLoadingKey,
		previousKey: newPreviousKey,
		markdownData,
	};
};

/**
 * Uses processMarkdownComponentMultiTags and recursively processes `unprocessedText`
 * after each tag boundary, so a single chunk containing multiple tags (e.g. a
 * close tag immediately followed by a new start tag, or tags for different
 * component types) is fully handled in one call.
 *
 * Returns `markdownData` as a map keyed by the component instance key (e.g.
 * `"chart-0"`)
 */
export const createMarkdownProcessorMultiTags = (
	getComponents: () => StreamableComponentConfig[],
): ((text: string) => {
	processedText: string;
	markdownData: MarkdownDataMap;
}) => {
	let currentLoadingKeys: Record<string, string | null> = {};
	let previousKeys: Record<string, string | null> = {};

	const processRecursive = (
		text: string,
		markdownData: MarkdownDataMap,
	): {
		processedText: string;
		markdownData: MarkdownDataMap;
	} => {
		for (const component of getComponents()) {
			if (!component.isEnabled) {
				continue;
			}

			const currentLoadingKey = currentLoadingKeys[component.key] ?? null;
			const prevKey = previousKeys[component.key] ?? null;

			const result = processMarkdownComponentMultiTags(text, currentLoadingKey, prevKey, component);

			if (!result.markdownData) {
				continue;
			}

			const { key, content, loading } = result.markdownData;
			currentLoadingKeys[component.key] = result.currentLoadingKey;
			previousKeys[component.key] = result.previousKey;

			const existing = markdownData[key];
			markdownData[key] = {
				content: (existing?.content ?? '') + content,
				loading,
			};

			if (result.unprocessedText) {
				const inner = processRecursive(result.unprocessedText, markdownData);
				return {
					...inner,
					processedText: result.processedText + inner.processedText,
				};
			}

			return {
				processedText: result.processedText,
				markdownData,
			};
		}

		// No component matched — return text unchanged.
		return {
			processedText: text,
			markdownData,
		};
	};

	return (
		text: string,
	): {
		processedText: string;
		markdownData: MarkdownDataMap;
	} => {
		return processRecursive(text, {});
	};
};
